import { IDType, EntityTable } from 'dexie'
import { from, Observable, of } from 'rxjs'
import { switchMap, map } from 'rxjs/operators'
import _ from 'lodash'

import { dbCacheDown } from './dexie/cache-down'
import { CACHE_DOWN_TABLE_NAMES } from './dexie/tableNames'
import { DataSynchronisationStatus, DataSynchronizerStats, EntityField, SearchResponse, getTranslatedString, SearchQuery } from '@skinsoft/types'
import { EntityFieldNotFoundError } from './offlineDataFetcher/errors'
import { itemSelectors, thesaurusTermSelectors } from '@skinsoft/selectors'

declare const config
const FALLBACK_LOCALE = config.fallback_locale

/* 
 * we can't use EntityFieldDataStore because it creates circular dependencies (as OfflineDataFetcher is used in DataStore)
 * so we have to access the EntityField offlineDataFetcher manually
 * this implies that EntityField must always be cached
*/
const entityFieldHelper = {
    getValueFromEntityField: async (value: any, entityField: EntityField, locale: string = FALLBACK_LOCALE): Promise<string> => {
        const efPath = _.get(entityField, 'path')
        const efIsReference = _.get(entityField, 'type.referenceField')
        const efType = _.get(entityField, 'type.type')

        const rawValue = _.get(value, efPath);

        if (!efIsReference) {
            switch (efType) {
                case "String":
                    return rawValue as string;
                case "TranslatedString":
                    return getTranslatedString(rawValue, locale) as string || ""
                case "SkinDate":
                    const returnValue: number = _.get(rawValue, 'startInterval.date') as number
                    return returnValue ? returnValue.toString() : "0"
                default:
                    return ""
            }
        } else {
            switch (efType) {
                case "Item":
                    const item = await dbCacheDown.item.get(rawValue)
                    if(item) {
                        return itemSelectors.title(item, locale)
                    }
                    return ''

                case "ThesaurusTerm":
                    const term = await dbCacheDown.thesaurusTerm.get(rawValue)
                    let returnValue = ''
                    if(term) {
                        returnValue = getTranslatedString(thesaurusTermSelectors.preferredTerm(term), locale) || ""
                    }
                    return returnValue;

                default:
                    return ""
            }
        }
    }
}

/**
 * T type returned by OfflineDataFetcher query
 * S type stored inside cache
 */
export class OfflineDataFetcher<T extends { id: string }, S extends { id: string } = T> {
    protected table: EntityTable<T, 'id'>
    statsKey: string
    private lastSynchroStats: DataSynchronizerStats = null as any

    constructor(table: EntityTable<T, 'id'>, statKey) {
        this.table = table
        this.statsKey = statKey
    }

    public static isTemporaryId(id: string) {
        return id && 
            (typeof id === 'string' || (id as any) instanceof String) && 
            id.startsWith('_')
    }

    public static async getLastGlobalSyncStats(): Promise<DataSynchronizerStats | undefined> {
        return dbCacheDown.syncDownStats.get("global")
    }

    public static async saveGlobalSyncStats(syncStats: DataSynchronizerStats) {
        await dbCacheDown.syncDownStats.put(
            syncStats,
            "global"
        )
    }

    public static async isSyncError(): Promise<boolean> {
        const stats = await this.getLastGlobalSyncStats()
        if (stats) {
            return stats.lastSynchroStatus === DataSynchronisationStatus.ERROR
                || stats.lastSynchroStatus === DataSynchronisationStatus.ABORTED
        }
        return true
    }

    public static async clear() {
        await dbCacheDown.tables.forEach(async table => {
            if(table.name !== 'syncDownStats' && table.name !== 'translation') {
                await table.clear()
            }
        })
    }

    public fetchOneFromId(id: string): Observable<T | null>  {
        return from(
            this.table.get(id as IDType<T, "id">)
        ).pipe(
            switchMap(res => {
                if(res) {
                    return of(res)
                }

                return of(null)
            }),
        )
    }

    public fetchOneFromPredicate(predicate: (data: T) => boolean): Observable<T> {
        return from(this.table.toArray()).pipe(
            map(allEntities => {
                const result = allEntities.find(entity => {
                    return predicate(entity)
                })
                if(!result) {
                    throw new Error("No data in cache for " + this.statsKey)
                }
                return result
            }),
        )
    }

    public fetchAllFromPredicate(predicate: (data: T) => boolean): Observable<T[]> {
        return from(
            this.table.filter(value => {
                return predicate(value)
            }).toArray()
        ) as Observable<T[]>
    }

    public async updateOne(id: string, entity: T): Promise<String> {
        return this.table.put(
            entity
        )
    }

    public async clear() {
        return this.table.clear()
    }

    public async append(dataToAppend: S[]) {
        return this.table.bulkAdd(dataToAppend as unknown as T[])
    }

    public async searchTitles(types: string[], filterText: string, pageNumber: number = 0, defaultFilter: string = "", size: number = 10, filterIds?: string[], esPath?: string, language: string = FALLBACK_LOCALE) {
        const aliasCacheKey = CACHE_DOWN_TABLE_NAMES['Alias']
        
        const filterPredicate = (entry: any, cacheKey: string) => {
            let match = true

            // Filter values whose 'esPath' value matches 'defaultFilter'
            if (defaultFilter && esPath) {
                const trailingRefIndex = esPath.lastIndexOf('.ref')
                let sanitizedEsPath = trailingRefIndex >= 0 ? esPath.substring(0, trailingRefIndex) : esPath // eliminating .ref from indexation if needed
                if(cacheKey.toUpperCase() === aliasCacheKey.toUpperCase()) {

                    let tags = _.get(entry, sanitizedEsPath, [])
                    if(!(tags && tags.length)) {
                        sanitizedEsPath = `alias.${sanitizedEsPath}`
                        tags = _.get(entry, sanitizedEsPath, [])
                    }

                    match = match && (tags.includes(defaultFilter) || tags.map(tag => tag?.ref).includes(defaultFilter))
                } else {
                    const valueAtEsPath = _.get(entry, esPath) ?? _.get(entry, sanitizedEsPath)
                    match = match && valueAtEsPath === defaultFilter
                }
            }

            // Filter values mathching some ids
            if (filterIds && filterIds.length > 0) {
                match = match && filterIds.includes(entry.id)
            }

            if (filterText) {
                match = match && this.titleMatcher(entry, filterText, language)
            }

            return match
        }
        
        let filteredData: any[] = []

        const cacheKeys = _.uniq(types.map(type => CACHE_DOWN_TABLE_NAMES[type]))
        for(const cacheKey of cacheKeys ) {
            const table = dbCacheDown[cacheKey]
            
            await table.each((value) => {
                if(filterPredicate(value, cacheKey)) {
                    if(cacheKey.toUpperCase() === aliasCacheKey.toUpperCase()) {
                        if((value as any).alias) {
                            filteredData.push((value as any).alias) // no idea why we get alias in some cases
                            return
                        }
                    }
                    filteredData.push(value)
                }
            })
        }

        const reshapedData = filteredData.map(
            value => {
                const reshapedValue = {
                    genericEntityTitle:
                    {
                        translatedString: {}
                    },
                    // @ts-ignore
                    id: value.id,
                }

                const title = this.getTitle(value, language)

                _.set(
                    reshapedValue,
                    `genericEntityTitle.translatedString.${language}`,
                    title
                )
                return reshapedValue
            }
        )

        const sortedData = reshapedData
            .sort(
                (titleA, titleB) => {
                    const titleAStr = _.get(titleA, `genericEntityTitle.translatedString.${language}`) || "...";
                    const titleBStr = _.get(titleB, `genericEntityTitle.translatedString.${language}`) || "...";

                    return titleAStr.localeCompare(titleBStr, language);
                }
            )
        const clampedData = sortedData.slice(pageNumber * size, pageNumber * size + size)

        return {
            results: clampedData,
            totalSize: sortedData.length,
        }
    }

    protected titleMatcher(entity: S, title: string, locale: string) {
        let entityTitle: string | null = this.getTitle(entity, locale)
        if(entityTitle === '...') {
            entityTitle = null
        }
        
        if(entityTitle) {
            return entityTitle
                .normalize('NFD')               // to ignores accentuation in matches
                .replace(/\p{Diacritic}/gu, '') // (ex: 'é' === 'e')
                .toUpperCase()
                .includes(
                    title
                    .normalize('NFD')
                    .replace(/\p{Diacritic}/gu, '')
                    .toUpperCase()
                )
        }
        return false
    }

    protected getTitle(entity: S, language: string = FALLBACK_LOCALE): string {
        return _.get(entity, `indexTitle.translatedString.${language}`) ?? 
            _.get(entity, `genericEntityTitle.translatedString.${language}`) ??
            "..."
    }

    /**
     * Tries to reproduce the behaviour of online search on cache data
     * @param query 
     */
    public searchAll(query: any, locallyCreatedObjects: S[], locallyUpdatedObjects: S[], onlyOnLocallyCreated: boolean = false, language: string = FALLBACK_LOCALE): Observable<SearchResponse<S>> {
        return of(null)
        .pipe(
            switchMap(async () => {
                let result: S[] = []

                if(!onlyOnLocallyCreated) {
                    await this.table.each( (value) => {
                        const keep: boolean = (value as any).id//await this.matchQuery(value as S, query)
                        if(keep) {
                            result.push(value as unknown as S)
                        }
                        return undefined
                    })
    
                    result = this.mergeUpdateCacheDataToSearchResults(result ? result : [], locallyUpdatedObjects)
                }
                
                if(locallyCreatedObjects) {
                    result = result.concat(
                        locallyCreatedObjects.filter(createdEntity => createdEntity.id)
                    )
                }

                const {reducedData, totalSize} = await this.applyFiltersAndSortsOnData(result, query, language)
                return {reducedData, totalSize}
            }),
            switchMap(
                (value: any) => {
                    return of({
                        allData: value.reducedData,
                        results: [],
                        totalSize: value.totalSize,
                    })
                }
            ),
        )
    }

    /**
     * Applies the necessary filters and sorts to given data, according to query options
     * @param data 
     * @param query 
     */
    private async applyFiltersAndSortsOnData(data: S[], query: any, language: string = FALLBACK_LOCALE): Promise<{ reducedData: S[], totalSize: number }> {
        let res = await this.applyFilters(data, query, language)
        res = await this.applyAdvancedSearch(res, query, language)
        res = await this.applySort(res, query, language)

        const totalSize = res.length
        // Pagination options
        if (query.pageSize) {
            if (query.page) {
                const startIndex = query.page * query.pageSize
                res = res.slice(startIndex, startIndex + query.pageSize)
            } else {
                res.splice(query.pageSize)
            }
        }

        return { reducedData: res, totalSize }
    }

    private async applyFilters(data: S[], query, language: string = FALLBACK_LOCALE) {
        let filteredData = data
        // filtering on ids
        if (query.filteredIds) {
            if (filteredData.length > 0) {
                filteredData = filteredData.filter(
                    // @ts-ignore
                    item => query.filteredIds.includes(item.id)
                )
            }
        }

        //filtering excludedIds
        if(query.excludedIds) {
            if (filteredData.length > 0) {
                filteredData = filteredData.filter(
                    // @ts-ignore
                    item => !query.excludedIds.includes(item.id)
                )
            }
        }

        // Filtering on property value
        if (query.pathAndValueToCheck) {
            filteredData = filteredData.filter(item => {
                const itemValue = _.get(item, query.pathAndValueToCheck.pathToValue) ?? _.get(item, query.pathAndValueToCheck.pathToValue + ".name") 
                if(itemValue === '') {
                    return query.pathAndValueToCheck.value === ''
                }
                return query.pathAndValueToCheck.value.includes(itemValue)
            })
        }

        // Managing query text
        if (query.queryText) {
            filteredData = await Promise.all(filteredData.map(
                async value => {
                    (value as any).tempQueryTextFilter = await this.queryTextFilter(value, query.queryText, language)
                    return value
                }
            ))

            filteredData = filteredData.filter(
                value => {
                    const keep = (value as any).tempQueryTextFilter
                    delete (value as any).tempQueryTextFilter
                    return keep
                }
            )
        }

        return filteredData
    }

    /**
     * Override this class to customize search by entity type
     * @param entity 
     * @param queryText 
     */
    protected async queryTextFilter(entity: S, queryText: string, locale: string): Promise<boolean> {
        return true;
    }

    private async applyAdvancedSearch(data: S[], query, language: string = FALLBACK_LOCALE) {
        // Managing advanced search
        if (query.advancedSearches && query.advancedSearches.length > 0) {
            const leftCriteria = query.advancedSearches[0].leftCriteria;
            const rightCriteria = query.advancedSearches[0].rightCriteria;
            const operator = query.advancedSearches[0].linkOperator;

            let advancedSearchPredicate = await this.buildAdvancedSearchPredicate(operator, leftCriteria, rightCriteria, language)

            const advancedSearchResult = data.filter(entity => advancedSearchPredicate(entity))
            return advancedSearchResult
        }
        return data
    }

    private async buildAdvancedSearchPredicate(linkOperator?: string, leftCriteria?, rightCriteria?, language?: string, level = 1): Promise<(entity: any) => boolean> {
        let leftPredicate = entity => true;
        let rightPredicate = entity => true;
        
        if(leftCriteria) {
            if(leftCriteria.linkOperator) {
                leftPredicate = await this.buildAdvancedSearchPredicate(leftCriteria.linkOperator, leftCriteria.leftCriteria, leftCriteria.rightCriteria, language, level+1)
            } else {
                const entityFieldId = leftCriteria.entityField;
                const entityField: EntityField | undefined = await dbCacheDown.entityField.get(entityFieldId)
                if(!entityField) {
                    throw new EntityFieldNotFoundError()
                }
                leftPredicate = this.buildPredicateFromEntityField(leftCriteria, entityField, language)
            }
        }

        if(linkOperator) {
            if(rightCriteria) {
                if(rightCriteria.linkOperator) {
                    rightPredicate = await this.buildAdvancedSearchPredicate(rightCriteria.linkOperator, rightCriteria.leftCriteria, rightCriteria.rightCriteria, language, level+1)
                } else {
                    const rightEntityFieldId = rightCriteria.entityField;
                    const rightEntityField: EntityField | undefined = await dbCacheDown.entityField.get(rightEntityFieldId)
                    if(!rightEntityField) {
                        throw new EntityFieldNotFoundError()
                    }
                    rightPredicate = this.buildPredicateFromEntityField(rightCriteria, rightEntityField, language)
                }
            }

            switch (linkOperator) {
                case "AND":
                    return entity => leftPredicate(entity) && rightPredicate(entity)
                case "OR":
                    return entity => leftPredicate(entity) || rightPredicate(entity)
                case "NOT":
                    return entity => !leftPredicate(entity)
            }
        }

        return leftPredicate
    }

    /**
     * Builds a predicate from a search criteria 
     * @param criteria 
     * @param entityField 
     */
    private buildPredicateFromEntityField(criteria, entityField, language: string = FALLBACK_LOCALE): (entity: any) => boolean {
        const applyOperator = (operator: string, pathToValue: string, entityValue: any): boolean => {
            switch(operator) {
                case 'is-missing':
                    return _.isNil(entityValue)
                case 'equals':
                    return entityValue === _.get(criteria, `value.${pathToValue}`)
                case 'contains':
                    let value = _.get(criteria, `value.${pathToValue}`)
                    if(typeof entityValue === 'string' || entityValue instanceof String) {
                        entityValue = entityValue.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase() // to ignores accentuation in matches (ex: 'é' === 'e')
                        if(value) {
                            value = value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase()
                        }
                    }
                    return entityValue.includes(value)
                case 'exists':
                    return !_.isNil(entityValue)
                default:
                    return false
            }
        }

        return (entity) => {
            const path = `${entityField.path}`;
            let entityValue = _.get(entity, `${path}.name`) || _.get(entity, path)
            const operator: string = _.get(criteria, 'linkOperator', _.get(criteria, 'operator'))

            if(!entityValue && operator !== 'is-missing') {
                return false
            }

            let pathToValue = ''
            switch(criteria.value.type) {
                case 'ref':
                    pathToValue = 'refId'
                    break
                case 'text':
                    pathToValue = 'text'
                    break
                /*case 'date':
                    break
                case 'path': 
                    break
                case 'bool':
                    break
                case 'search-on-ids':
                    break*/
                default:
                    if('refId' in criteria.value) {
                        pathToValue = 'refId'
                    }
                    else {
                        pathToValue = 'text'
                    }
                    break
            }

            switch (entityField?.type?.type) {
                case "LifecycleState":
                    return criteria.value.text.includes(entityValue)
                case "LinkableArchiveItem":
                    entityValue = entityValue?.linkableItem ?? entityValue?.ref // ref/referenceType is the es equivalent of linkableItem/linkableItemReferenceType for LinkableArchiveItem
                    break
                case "TranslatedString":
                    entityValue = entityValue && getTranslatedString(entityValue, language, FALLBACK_LOCALE)
                    break
                
                case "List":
                    if(criteria.value?.type === "ref") {
                        return entityValue.includes(criteria.value.refId)
                    }
                    let predicate = (value) => applyOperator(operator, pathToValue, value)
                    switch (entityField?.type?.entityFieldType?.type) {
                        case "TranslatedString":
                            predicate = translatedString => {
                                const value = translatedString && _.get(translatedString, ['translatedString', language], "")
                                return applyOperator(operator, pathToValue, value)
                            }
                            break
                        case "LifecycleState":
                            predicate = value => criteria.value.text.includes(value)
                            break
                        case "LinkableArchiveItem":
                            criteria = value => applyOperator(operator, pathToValue, value?.linkableItem)
                            break
                    }
                    if(operator === 'is-missing') {
                        return entityValue.every(predicate)
                    }
                    return entityValue.some(predicate)
            }

            return applyOperator(operator, pathToValue, entityValue)
        }
    }

    private async applySort(data: S[], query, language: string = FALLBACK_LOCALE) {
        // Manage sorting
        if (query.sortObject) {
            const sortEntityFields: EntityField[] = []

            for(const entityFieldId of query.sortObject.entityFields) {
                const sortEntityField = await dbCacheDown.entityField.get(entityFieldId)
                if(!sortEntityField) {
                    throw new EntityFieldNotFoundError()
                }
                sortEntityFields.push(sortEntityField)
            }

            // Resolving temporay sort key
            const dataWithSortingkey = await Promise.all(data.map(
                async entity => {
                    if (!entity) {
                        entity = {} as unknown as S
                    }

                    let sortKey: string = ""

                    for(const sortEntityField of sortEntityFields) {

                        let fieldValue = await entityFieldHelper.getValueFromEntityField(
                            entity, 
                            sortEntityField, 
                            language
                        )

                        fieldValue = (fieldValue as any)?.valueToSort ?? fieldValue

                        if(fieldValue && ((fieldValue as any) instanceof String || typeof fieldValue === "string")) {
                            sortKey = fieldValue
                            break
                        }
                    }

                    (entity as any).tempSortKey = sortKey
                    return entity
                }
            ))

            const sortedData = dataWithSortingkey
                .sort(
                    (value1, value2) => {
                        return query.sortObject.sortAsc ?
                            (value1 as any).tempSortKey.localeCompare((value2 as any).tempSortKey, language) :
                            (value2 as any).tempSortKey.localeCompare((value1 as any).tempSortKey, language);
                    }
                )
                .map(
                    value => {
                        delete (value as any).tempSortKey;
                        return value;
                    }
                )

            return sortedData
        }

        return data
    }

    public async insertLocallyCreatedDataInOnlineSearchResult(onlineData: S[], offlineData: S[], query: SearchQuery, locale: string) {
        const onlineAndOfflineData = onlineData.concat(offlineData)
        return this.applySort(onlineAndOfflineData, query, locale)
    } 

    /**
     * Merges entities with their equivalent in cache, the correspondance is done by the 'id' field
     * @param fetchedData 
     * @param locallyUpdatedData 
     */
    private mergeUpdateCacheDataToSearchResults(fetchedData: S[], locallyUpdatedData: S[]) {
        for (let fetchedEntity of fetchedData) {
            // @ts-ignore
            const cachedUpdatedValue = locallyUpdatedData.filter(data => data.id === fetchedEntity.id)
            if (cachedUpdatedValue) {
                fetchedEntity = this.mergeCacheToEntity(fetchedEntity, cachedUpdatedValue[0])
            }
        }
        return fetchedData;
    }

    /**
     * Merges cache data with an entity to take all modifications into account
     * @param entity 
     * @param cachedValue 
     */
    private mergeCacheToEntity(entity: any, cachedValue: any) {
        const cachedCurrentState = _.get(cachedValue, 'workflowSchema.currentState')
        const mergedResult = _.merge(entity, cachedValue)

        if (cachedCurrentState) {
            _.set(mergedResult, 'workflowSchema.currentState', cachedCurrentState);
        }

        return mergedResult;
    }

    public saveStats(synchroStats: DataSynchronizerStats) {
        this.lastSynchroStats = synchroStats
        dbCacheDown.syncDownStats.put(
            synchroStats,
            this.statsKey
        )
    }

    private async fetchLastSynchroStats() {
        this.lastSynchroStats = (await dbCacheDown.syncDownStats.get(this.statsKey)) ?? null as any
    }

    public getLastSynchroStats() {
        return this.lastSynchroStats
    }
}